import { currencySchema, PAYMENT_LINK_STATUSES } from '@btsis/core';
import { oc } from '@orpc/contract';
import { z } from 'zod';
import { commonErrors } from './common/errors.js';
import { moneySchema } from './common/money.js';
import { paymentProviderSchema } from './payment-credential.js';

const guarded = {
  UNAUTHENTICATED: commonErrors.UNAUTHENTICATED,
  PERMISSION_DENIED: commonErrors.PERMISSION_DENIED,
  OUT_OF_SCOPE: commonErrors.OUT_OF_SCOPE,
  NOT_FOUND: commonErrors.NOT_FOUND,
  CONFLICT: commonErrors.CONFLICT,
  VALIDATION_FAILED: commonErrors.VALIDATION_FAILED,
};

export const organizationRefSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  name: z.string(),
});

export type OrganizationRef = z.infer<typeof organizationRefSchema>;

/**
 * One account a link may be drawn on, reduced to exactly six fields: id,
 * label, provider, currency and the organization it belongs to — an id and a
 * name, because a donation that has not been paid yet is offered the accounts
 * of every organization its institution is linked to
 * (docs/decisions/0112 §4). **No IBAN, no external reference and no
 * credential state beyond being offered at all** — an institution-level
 * operator reads this through donation scope, and donation scope justifies the
 * donation's own organization and nothing more (docs/decisions/0053 §2).
 */
export const payableAccountSchema = z.object({
  id: z.uuid(),
  label: z.string(),
  provider: paymentProviderSchema,
  currency: currencySchema,
  organizationId: z.uuid(),
  organizationName: z.string(),
});

export type PayableAccount = z.infer<typeof payableAccountSchema>;

/**
 * What the operator's screen shows about a link. `url` is here because the
 * whole point is to hand it to a donor — it is a bearer token to a payment, so
 * it is never logged in full and never written into an audit row.
 *
 * `lent` is the consequence the screen must state: the account belongs to
 * another organization, so the capture will be written unmatched there and
 * carried home by a pass-through fund transfer, and **the donation stays unpaid
 * and carries no receipt until that transfer completes** —
 * docs/decisions/0053 *Amended 2026-09-17*.
 */
export const paymentLinkSchema = z.object({
  id: z.uuid(),
  provider: paymentProviderSchema,
  url: z.string(),
  status: z.enum(PAYMENT_LINK_STATUSES),
  amount: moneySchema,
  expiresAt: z.string(),
  createdAt: z.string(),
  createdByName: z.string().nullable(),
  account: payableAccountSchema,
  organization: organizationRefSchema,
  accountOrganization: organizationRefSchema,
  lent: z.boolean(),
  fundTransferId: z.uuid().nullable(),
});

export type PaymentLink = z.infer<typeof paymentLinkSchema>;

const accountsOutput = z.object({
  organization: organizationRefSchema.nullable(),
  currency: currencySchema,
  accounts: z.array(payableAccountSchema),
});

/**
 * `organization` is the organization the donation speaks for. `organizations`
 * is every organization whose accounts `accounts` lists: the donation's own once
 * it has one, otherwise each organization its institution is linked to — a
 * link drawn on any of those is not lent (docs/decisions/0112 §4).
 */
const offeredAccountsOutput = accountsOutput.extend({
  organizations: z.array(organizationRefSchema),
  mayLendAccount: z.boolean(),
});

/**
 * A donation's hosted-checkout link — docs/plans/06-portal-and-field.md §6.2
 * block B, docs/decisions/0053 §5.
 *
 * Every route here takes a **donation id and never an organization id**, except
 * `lendableAccounts`, which names one on purpose: that is the second,
 * explicitly named read, answered only in the lending organization's own scope
 * and only to a caller holding `organization.lendAccount`.
 */
export const paymentLinkContract = {
  current: oc
    .route({
      method: 'GET',
      path: '/donations/{donationId}/payment-link',
      summary: 'The donation’s live link, or the last one it had',
    })
    .errors(guarded)
    .input(z.object({ donationId: z.uuid() }))
    .output(
      z.object({
        link: paymentLinkSchema.nullable(),
        live: z.boolean(),
        paymentPageUrl: z.string().nullable(),
      }),
    ),

  accounts: oc
    .route({
      method: 'GET',
      path: '/donations/{donationId}/payment-link/accounts',
      summary:
        'The usable provider accounts of the donation’s organization, or of every organization its institution is linked to, in its own currency',
    })
    .errors(guarded)
    .input(z.object({ donationId: z.uuid() }))
    .output(offeredAccountsOutput),

  lendableAccounts: oc
    .route({
      method: 'GET',
      path: '/donations/{donationId}/payment-link/lendable-accounts',
      summary: 'Another organization’s accounts, read in that organization’s own scope',
    })
    .errors(guarded)
    .input(z.object({ donationId: z.uuid(), organizationId: z.uuid() }))
    .output(accountsOutput),

  create: oc
    .route({
      method: 'POST',
      path: '/donations/{donationId}/payment-link',
      summary: 'Draw a link on an account. Asking again while one is live returns the same URL',
    })
    .errors(guarded)
    .input(z.object({ donationId: z.uuid(), organizationAccountId: z.uuid() }))
    .output(z.object({ link: paymentLinkSchema, created: z.boolean() })),

  regenerate: oc
    .route({
      method: 'POST',
      path: '/donations/{donationId}/payment-link/regenerate',
      summary: 'Expire the live link and issue a new one, so the old URL can no longer pay',
    })
    .errors(guarded)
    .input(z.object({ donationId: z.uuid(), organizationAccountId: z.uuid() }))
    .output(z.object({ link: paymentLinkSchema, created: z.boolean() })),
};
